import React, { useMemo, useState } from "react";
import { withStyles } from "@mui/styles";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridPrintExportMenuItem,
  GridCsvExportMenuItem,
  GridToolbarExportContainer,
} from "@mui/x-data-grid";
//import { DataGridPremium } from "@mui/x-data-grid-premium";
import { styled } from "@mui/material/styles";
import { Box, Button, Stack } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import uuid from "react-uuid";
import { numberFormat } from "app/utils/utils";

const isConversionMetricField = (field = "") =>
  field === "conversions" || /^conversions_\d+$/.test(field);

const isRevenueMetricField = (field = "") => /^roi_\d+$/.test(field);

const formatMetricCount = (value) => numberFormat(Number(value || 0));

const formatMetricCurrency = (value) =>
  `$${numberFormat(Number(value || 0).toFixed(2))}`;

const formatPercentDisplay = (value) => {
  const numericValue = Number(value || 0);
  const boundedValue = Math.max(0, Math.min(100, numericValue));
  return `${numberFormat(boundedValue.toFixed(2))}%`;
};

const getCtrDisplay = (row = {}) => {
  if (row?.ctr !== undefined && row?.ctr !== null && row?.ctr !== "") {
    return formatPercentDisplay(row?.ctr);
  }

  const impressions = Number(row?.impressions || 0);
  const clicks = Number(row?.clicks || 0);
  if (impressions <= 0 || clicks <= 0) {
    return "0%";
  }

  return formatPercentDisplay((clicks / impressions) * 100);
};

const getViewabilityDisplay = (row = {}) => {
  if (row?.viewability !== undefined && row?.viewability !== null && row?.viewability !== "") {
    return formatPercentDisplay(row?.viewability);
  }

  if (row?.viewables === undefined || row?.viewables === null || row?.viewables === "") {
    return "";
  }

  const impressions = Number(row?.impressions || 0);
  const viewables = Number(row?.viewables || 0);
  if (impressions <= 0 || viewables <= 0) {
    return "";
  }

  return formatPercentDisplay((viewables / impressions) * 100);
};

const getVcrDisplay = (row = {}) => {
  if (row?.vcr !== undefined && row?.vcr !== null && row?.vcr !== "") {
    return formatPercentDisplay(row?.vcr);
  }

  if (row?.complete === undefined || row?.complete === null || row?.complete === "") {
    return "";
  }

  const impressions = Number(row?.impressions || 0);
  const completes = Number(row?.complete || 0);
  if (impressions <= 0) {
    return "0%";
  }

  return formatPercentDisplay((completes / impressions) * 100);
};

const getEcpmDisplay = (row = {}) => {
  const ecpm = Number(row?.ecpm || 0);
  if (ecpm > 0) {
    return formatMetricCurrency(ecpm);
  }

  const impressions = Number(row?.impressions || 0);
  const spend = Number(row?.spend || 0);
  if (impressions <= 0 || spend <= 0) {
    return "$0";
  }

  return formatMetricCurrency((spend * 1000) / impressions);
};

const getPerConversionCostDisplay = (row = {}, field = "conversions") => {
  const spend = Number(row?.spend || 0);
  const conversions = Number(row?.[field] || 0);
  if (spend <= 0 || conversions <= 0) {
    return "$0";
  }

  return formatMetricCurrency(spend / conversions);
};

const getRevenueMetricLabel = (column = {}) => {
  const headerName = `${column?.headerName || ""}`;
  const match = headerName.match(/^Revenue\s*\((.+)\)$/i);
  if (match?.[1]) {
    return match[1];
  }

  return headerName || column?.field || "Revenue";
};

const getRevenueBreakdownDisplay = (row = {}, revenueColumns = []) => {
  if (!Array.isArray(revenueColumns) || revenueColumns.length === 0) {
    return "";
  }

  return revenueColumns
    .map((column) => formatMetricCurrency(row?.[column.field] || 0))
    .join(" / ");
};

const getPerConversionCostLabel = (column = {}) => {
  const compactLabel = `${column?.headerName || column?.field || ""}`
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();

  if (compactLabel === "SP") {
    return "CPSP";
  }

  if (compactLabel === "FTD") {
    return "CPFTD";
  }

  if (compactLabel === "RD") {
    return "CPRD";
  }

  return "Cost";
};

const sanitizeGridColumn = (column = {}) => {
  const nextColumn = { ...column };

  ["minWidth", "maxWidth", "width"].forEach((dimensionKey) => {
    if (nextColumn?.[dimensionKey] === undefined) {
      return;
    }

    const numericValue = Number(nextColumn[dimensionKey]);
    if (Number.isFinite(numericValue) && numericValue >= 0) {
      nextColumn[dimensionKey] = numericValue;
    } else {
      delete nextColumn[dimensionKey];
    }
  });

  if (nextColumn?.flex !== undefined) {
    const numericFlex = Number(nextColumn.flex);
    if (Number.isFinite(numericFlex) && numericFlex > 0) {
      nextColumn.flex = numericFlex;
    } else {
      delete nextColumn.flex;
    }
  }

  return nextColumn;
};

const appendUniqueColumn = (accumulator, seenFields, column) => {
  const sanitizedColumn = sanitizeGridColumn(column);
  const field = sanitizedColumn?.field;

  if (field) {
    if (seenFields.has(field)) {
      return accumulator;
    }
    seenFields.add(field);
  }

  accumulator.push(sanitizedColumn);
  return accumulator;
};

const buildStackedMetricColumn = (
  column,
  secondaryLabel,
  getPrimaryDisplay,
  getSecondaryDisplay
) => ({
  ...column,
  renderHeader: () => (
    <Box className="report-stacked-header">
      <Box component="span" className="report-stacked-header-primary">
        {column?.headerName || column?.field}
      </Box>
      <Box component="span" className="report-stacked-header-secondary">
        {secondaryLabel}
      </Box>
    </Box>
  ),
  renderCell: (params) => (
    <Box className="report-stacked-cell">
      <Box component="span" className="report-stacked-cell-primary">
        {getPrimaryDisplay(params?.row || {}, params)}
      </Box>
      <Box component="span" className="report-stacked-cell-secondary">
        {getSecondaryDisplay(params?.row || {}, params)}
      </Box>
    </Box>
  ),
  valueFormatter: (params) => {
    const row = params?.row || {};
    const primaryValue = getPrimaryDisplay(row, params);
    const secondaryValue = getSecondaryDisplay(row, params);
    return secondaryValue
      ? `${primaryValue} | ${secondaryValue}`
      : `${primaryValue}`;
  },
});

const buildReportingColumns = (
  columns = [],
  rows = [],
  stackReportingMetrics = false
) => {
  if (!stackReportingMetrics || !Array.isArray(columns) || columns.length === 0) {
    const seenFields = new Set();
    return (Array.isArray(columns) ? columns : []).reduce((accumulator, column) => {
      return appendUniqueColumn(accumulator, seenFields, column);
    }, []);
  }

  const fields = new Set(columns.map((column) => column?.field).filter(Boolean));
  const seenFields = new Set();
  const hasVisibleViewabilityData =
    fields.has("viewables") ||
    (Array.isArray(rows) &&
      rows.some((row) => {
        const explicitViewability = row?.viewability;
        if (
          explicitViewability !== undefined &&
          explicitViewability !== null &&
          explicitViewability !== "" &&
          Number(explicitViewability) > 0
        ) {
          return true;
        }

        const viewables = row?.viewables;
        return viewables !== undefined && viewables !== null && Number(viewables) > 0;
      }));
  const revenueBreakdownColumns = columns.filter((column) =>
    isRevenueMetricField(column?.field)
  );
  const revenueBreakdownLabel = revenueBreakdownColumns
    .map((column) => getRevenueMetricLabel(column))
    .join(" / ");
  const hasInlineConversionMetrics = Array.from(fields).some((field) =>
    isConversionMetricField(field)
  );
  const canStackViewability = fields.has("impressions") && hasVisibleViewabilityData;
  const canStackCtr = fields.has("clicks");
  const canStackVcr = fields.has("complete");
  const canStackSpend = fields.has("spend");
  const spendSecondaryField = fields.has("net") ? "net" : fields.has("ecpm") ? "ecpm" : "";
  const canStackRevenueBreakdown =
    fields.has("roi") && revenueBreakdownColumns.length > 0;
  const canShowPerConversionCost =
    fields.has("spend") ||
    fields.has("conversion_cost_gross") ||
    fields.has("ccg");

  return columns.reduce((accumulator, column) => {
    const field = column?.field;

    if (!field) {
      return appendUniqueColumn(accumulator, seenFields, column);
    }

    if (field === "viewables" && canStackViewability) {
      return accumulator;
    }

    if (field === "ctr" && canStackCtr) {
      return accumulator;
    }

    if (field === "vcr" && canStackVcr) {
      return accumulator;
    }

    if (isRevenueMetricField(field) && canStackRevenueBreakdown) {
      return accumulator;
    }

    if (field === "net" && spendSecondaryField === "net") {
      return accumulator;
    }

    if (field === "ecpm" && spendSecondaryField === "ecpm") {
      return accumulator;
    }

    if (
      hasInlineConversionMetrics &&
      fields.has("spend") &&
      (field === "conversion_cost_gross" || field === "ccg")
    ) {
      return accumulator;
    }

    if (field === "impressions" && canStackViewability) {
      return appendUniqueColumn(
        accumulator,
        seenFields,
        buildStackedMetricColumn(
          column,
          "Viewability %",
          (row) => formatMetricCount(row?.impressions),
          (row) => getViewabilityDisplay(row)
        )
      );
    }

    if (field === "clicks" && canStackCtr) {
      return appendUniqueColumn(
        accumulator,
        seenFields,
        buildStackedMetricColumn(
          column,
          "CTR",
          (row) => formatMetricCount(row?.clicks),
          (row) => getCtrDisplay(row)
        )
      );
    }

    if (field === "complete" && canStackVcr) {
      return appendUniqueColumn(
        accumulator,
        seenFields,
        buildStackedMetricColumn(
          column,
          "VCR",
          (row) => formatMetricCount(row?.complete),
          (row) => getVcrDisplay(row)
        )
      );
    }

    if (field === "spend" && canStackSpend && spendSecondaryField) {
      return appendUniqueColumn(
        accumulator,
        seenFields,
        buildStackedMetricColumn(
          column,
          spendSecondaryField === "net" ? "Net" : "eCPM",
          (row) => formatMetricCurrency(row?.spend),
          (row) =>
            spendSecondaryField === "net"
              ? formatMetricCurrency(row?.net)
              : getEcpmDisplay(row)
        )
      );
    }

    if (field === "roi" && canStackRevenueBreakdown) {
      return appendUniqueColumn(
        accumulator,
        seenFields,
        buildStackedMetricColumn(
          {
            ...column,
            headerName: "Total Revenue",
          },
          revenueBreakdownLabel,
          (row) => formatMetricCurrency(row?.roi),
          (row) => getRevenueBreakdownDisplay(row, revenueBreakdownColumns)
        )
      );
    }

    if (
      canShowPerConversionCost &&
      column?.secondaryMetricVisible !== false &&
      isConversionMetricField(field)
    ) {
      return appendUniqueColumn(
        accumulator,
        seenFields,
        buildStackedMetricColumn(
          column,
          column?.secondaryMetricLabel || getPerConversionCostLabel(column),
          (row) => formatMetricCount(row?.[field]),
          (row) => getPerConversionCostDisplay(row, field)
        )
      );
    }

    return appendUniqueColumn(accumulator, seenFields, column);
  }, []);
};

function CustomNoRowsOverlay() {
  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      <div style={{ fontSize: "14px" }}>No Data Available</div>
    </Stack>
  );
}

const AnalyticsDashboardCustomButtons = (props) => {
  return (
    <div style={{ display: "flex", marginLeft: "10px" }}>
      <Box sx={{ mr: 2 }}>
        <Button
          variant="outlined"
          color="success"
          startIcon={<DoneIcon />}
          onClick={() => {
            props.clickhandler("selButton");
          }}
        >
          Selected
        </Button>
      </Box>
      <Box>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DoNotDisturbIcon />}
          onClick={() => {
            props.clickhandler("rejButton");
          }}
        >
          Rejected
        </Button>
      </Box>
    </div>
  );
};

const CustomExportButton = (props) => (
  <GridToolbarExportContainer {...props}>
    <GridCsvExportMenuItem />
    <GridPrintExportMenuItem
      options={{
        hideFooter: true,
        hideToolbar: true,
      }}
    />
  </GridToolbarExportContainer>
);

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 0,
  color: "rgba(0,0,0,.85)",

  fontFamily: ["NoirPro", "Arial"].join(","),
  WebkitFontSmoothing: "auto",
  letterSpacing: "normal",
  "& .MuiDataGrid-columnHeaders": {
    borderBottom: "1px solid rgb(224, 224, 224)",
  },
  "& .MuiDataGrid-columnsContainer": {
    backgroundColor: "#fafafa",
  },
  "& .MuiDataGrid-iconSeparator": {
    display: "none",
  },
  "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
    borderRight: `1px solid ${"#f0f0f0"}`,
  },
  "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
    borderBottom: `1px solid ${"#f0f0f0"}`,
  },
  "& .MuiDataGrid-columnHeadersInner .MuiDataGrid-columnHeader:last-child": {
    borderRight: "none !important",
  },
  "& .MuiDataGrid-cell": {
    alignItems: "center",
    lineHeight: "1.4 !important",
    maxHeight: "none !important",
    overflow: "visible",
    py: 1,
    wordBreak: "normal",
    whiteSpace: "normal",
  },
  "& .MuiDataGrid-cellContent": {
    lineHeight: "1.4",
    overflow: "hidden",
    overflowWrap: "break-word",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
  },
  "& .campaign-actions-cell": {
    alignItems: "stretch",
    overflowX: "auto !important",
    overflowY: "hidden !important",
    py: "4px !important",
  },
  "& .campaign-actions-cell .MuiDataGrid-cellContent": {
    display: "block",
    overflow: "visible",
    whiteSpace: "nowrap",
    width: "100%",
  },
  "& .MuiDataGrid-columnHeader": {
    alignItems: "center",
    minHeight: "56px !important",
    whiteSpace: "normal",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    lineHeight: 1.3,
    whiteSpace: "normal",
    fontWeight: 700,
  },
  "&.reporting-stacked-grid .MuiDataGrid-columnHeader": {
    minHeight: "68px !important",
  },
  "&.reporting-stacked-grid .MuiDataGrid-cell": {
    py: 1.25,
  },
  "&.reporting-stacked-grid .MuiDataGrid-row:hover": {
    backgroundColor: "#f8fafc",
  },
  "& .report-stacked-header": {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.15,
    width: "100%",
  },
  "& .report-stacked-header-primary": {
    fontSize: "13px",
    fontWeight: 700,
    color: "#111827",
  },
  "& .report-stacked-header-secondary": {
    fontSize: "11px",
    color: "#6b7280",
    fontWeight: 500,
    marginTop: "2px",
  },
  "& .report-stacked-cell": {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    lineHeight: 1.15,
    width: "100%",
    minHeight: "52px",
  },
  "& .report-stacked-cell-primary": {
    fontSize: "14px",
    fontWeight: 700,
    color: "#111827",
  },
  "& .report-stacked-cell-secondary": {
    fontSize: "11px",
    color: "#6b7280",
    fontWeight: 500,
    marginTop: "4px",
  },
  "& .MuiPaginationItem-root": {
    borderRadius: 0,
  },
  "& .MuiDataGrid-renderingZone": {
    maxHeight: "none !important",
    fontSize: "14px",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid rgb(224, 224, 224)",
  },
  // '& .campaign-active': {
  //   color: "green",

  // },
  // '& .campaign-draft': {
  //   color: "#17a2b8",
  // },
  // '& .campaign-pause': {
  //   color: "orange"

  // },
  // '& .campaign-expire': {
  //   color: "#e74c3c"
  // },
  "& .campaign-schedule": {
    color: "#4C0FB0",
  },
  // '& .campaign-approval_pending': {
  //   color: "deeppink"
  // },
  // ...customCheckbox(theme),
}));

// const StyledDataGrid = withStyles({
//   root: {
//     "& .MuiDataGrid-root": {
//       fontFamily: "NoirPro,Arial",
//     },
//     "& .MuiButtonBase-root": {
//       fontFamily: "NoirPro,Arial",
//     },
//     "& .MuiDataGrid-main": {
//       //border: "1px solid #ccc !important",
//       borderRadius: "none !important",
//       borderTopRightRadius: "4px !important",
//       borderTopLeftRadius: "4px !important",
//       fontFamily: "NoirPro,Arial",
//     },
//     "& .MuiDataGrid-renderingZone": {
//       maxHeight: "none !important",
//       fontSize: "14px",
//     },
//     "& .MuiDataGrid-cell": {
//       lineHeight: "unset !important",
//       maxHeight: "none !important",
//       whiteSpace: "normal !important",
//       textOverflow: "clip",
//       borderBottom: "1px solid #ccc !important",
//       borderRight: "1px solid #ccc",
//       padding: "0 0 0 10px !important",
//       "&:last-child": {
//         borderRight: "none",
//       },
//       "&:first-child": {
//         paddingLeft: "20px !important",
//       },
//       wordBreak: "break-all",
//     },
//     "& .MuiDataGrid-row": {
//       maxHeight: "none !important",
//       fontSize: "14px !important",
//     },
//     "& .MuiDataGrid-columnHeaders": {
//       fontSize: "14px !important",
//       borderBottom: "1px solid #ccc",
//       borderTop: "1px solid #ccc",
//       borderTopLeftRadius: "0px",
//       borderTopRightRadius: "0px",
//       padding: "0 0 0 10px !important",
//     },
//     "& .MuiDataGrid-columnHeaderTitleContainerContent:first-child": {
//       paddingLeft: "14px !important",
//     },
//     "& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeaderDraggableContainer ":
//       {
//         //borderRight: "1px solid #ccc",
//       },
//     "& .MuiDataGrid-columnHeaderDraggableContainer": {
//       borderRight: "1px solid #ccc",
//     },
//     "& .MuiDataGrid-columnHeaderTitleContainer": {
//       borderRight: "1px solid #ccc",
//     },
//     "& .MuiDataGrid-columnHeaders .MuiDataGrid-menuIcon:last-child": {
//       marginRight: "8px !important",
//     },
//     "&.MuiDataGrid-footerContainer,.MuiToolbar-root": {
//       fontSize: "14px !important",
//     },
//     "&.MuiDataGrid-footerContainer": {
//       borderBottom: "1px solid #ccc",
//     },
//     "& .MuiDataGrid-columnSeparator .MuiSvgIcon-root": {
//       color: "grey !important",
//     },
//     "& .MuiDataGrid-footerContainer": {
//       border: "1px solid #ccc !important",
//       borderTop: "none !important",
//       borderRadius: "none !important",
//       borderBottomRightRadius: "4px !important",
//       borderBottomLeftRadius: "4px !important",
//     },
//     "& .MuiDataGrid-columnHeader:focus,.MuiDataGrid-cell:focus": {
//       outline: "none !important",
//     },
//     "& .MuiDataGrid-overlay .MuiCircularProgress-root": {
//       width: "60px !important",
//       height: "60px !important",
//     },
//     // "& .MuiDataGrid-cell:first-child": {
//     //   paddingLeft: "20px !important",
//     // },
//     "& .MuiDataGrid-columnSeparator": {
//       display: "none",
//     },
//     "& .MuiDataGrid-cell .MuiDataGrid-cell--textLeft": {
//       // minHeight: "35px !important",
//       // maxHeight: "35px !important",
//     },
//   },
// })(DataGrid);
const tableHeight = window.innerHeight - 250;
const Datagrid = ({
  rows,
  columns,
  isloading,
  footer,
  showDownloadButtons,
  rowId,
  page,
  checkbox,
  checkBoxHandler,
  defaultSelectedIds,
  fileName,
  stackReportingMetrics,
}) => {
  //console.log(rows);
  // isloading,
  const [selectionModel, setSelectionModel] = useState(defaultSelectedIds);

  // console.log("selectionModel:", selectionModel);
  // console.log("defaultSelectedIds:", defaultSelectedIds);

  function CustomToolbar(props) {
    if (showDownloadButtons) {
      return (
        <GridToolbarContainer sx={{ ml: 2 }}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <CustomExportButton {...props} />
          <AnalyticsDashboardCustomButtons
            clickhandler={props?.analyticsselclick}
          />
        </GridToolbarContainer>
      );
    } else {
      return <></>;
    }
  }
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 50,
    page: 0,
  });
  const enhancedColumns = useMemo(
    () => buildReportingColumns(columns, rows, stackReportingMetrics),
    [columns, rows, stackReportingMetrics]
  );
  const showsVideoCompleteNote = Array.isArray(columns)
    ? enhancedColumns.some((column) => column?.field === "complete")
    : false;

  const CustomExportButton = (props) => (
    <GridToolbarExportContainer {...props}>
      <GridCsvExportMenuItem
        options={{ fileName: props?.csvexportoptions?.filename }}
      />
      {/* <GridPrintExportMenuItem
        options={{
          hideFooter: true,
          hideToolbar: true,
        }}
      /> */}
    </GridToolbarExportContainer>
  );
  function generateRandom() {
    var length = 8,
      charset =
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
      retVal = "";
    for (var i = 0, n = charset.length; i < length; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * n));
    }
    return retVal;
  }
  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {showsVideoCompleteNote && (
        <Box sx={{ fontSize: "11px", color: "#6b7280", mb: 1 }}>
          Video Complete applies to video creatives only, not display creatives.
        </Box>
      )}
      <Box sx={{ flex: 1, minHeight: 0, width: "100%", minWidth: 0 }}>
        <StyledDataGrid
          rows={rows}
          columns={enhancedColumns}
          className={stackReportingMetrics ? "reporting-stacked-grid" : ""}
          // pageSize={rowsPerPage}
          // rowsPerPageOptions={[1, 2, 3, 6, 8, 10, 100]}
          style={{ width: "100%", height: "100%", border: "none" }}
          // hideFooterSelectedRowCount
          //hideFooter={false}
          // onPageSizeChange={(pageSize) => {
          //   setRowsPerPage(pageSize);
          // }}
          getRowClassName={(params) =>
            page == "campaignList" ? `campaign-${params.row.status_type}` : ""
          }
          loading={isloading}
          // getCellClassName={(params) => {
          //   if (params.field === "status_type") {
          //     if (params.value == "Active") {
          //       return "status_active";
          //     } else if (params.value == "InActive") {
          //       return "status_inactive";
          //     }
          //     return "";
          //   }
          // }}
          checkboxSelection={checkbox}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          getRowId={(row) => (rowId ? generateRandom() : row.id)}
          components={{
            Toolbar: CustomToolbar,
            NoRowsOverlay: CustomNoRowsOverlay,
          }}
          componentsProps={{
            toolbar: {
              analyticsselclick: checkBoxHandler,
              csvexportoptions: {
                filename:
                  fileName != ""
                    ? fileName + "_" + uuid()
                    : "Mobiquity_ATOS_" + uuid(), // Specify the desired filename for the downloaded CSV file
              },
            },
          }}
          getRowHeight={() => "auto"}
          onSelectionModelChange={(ids) => {
            //console.log("ids:", ids);
            //let rowId = ids[0];

            if (typeof checkBoxHandler === "function") {
              setSelectionModel(ids);
              checkBoxHandler("checkbox", { selIds: ids });
            }
          }}
          selectionModel={selectionModel}
        />
      </Box>
    </Box>
  );
};

export default Datagrid;
